import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {SalesPersonsAccessLevelsDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import {Box, Card, CircularProgress, Divider, Fade, MenuItem, Skeleton, SvgIcon, TextField, Tooltip, Typography} from "@mui/material";
import {AssessmentOutlined} from "@mui/icons-material";
import {Switch} from "../../../../../../components/base/switch";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import SearchInput from "../../../../../../components/base/input/search_input";
import Api from "../../../../../../../core/services/api/api";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import CompetitorPriceReportsPopover from "../../../../../../components/app-specific/draggable-popover/competitor-price-reports-popover";
import {useOperatorInformation} from "../../../../../../hooks/use-operator-information";


const dataGridSaveConfig = {
    name: 'SalesPersonsAccessLevelsViewDataGrid',
    version: 4,
}

const SalesPersonsAccessLevelsView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.sales-persons-access-levels'});
    const operatorAccessLevels = useAccessLevels();
    const operatorInformation = useOperatorInformation();
    const currentOperatorId = operatorInformation?.id;
    const canViewAllCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports;
    const canViewMyCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports;
    const competitorPriceReportsMineOnly = !canViewAllCompetitorPriceReports && canViewMyCompetitorPriceReports;
    const canViewAnyCompetitorPriceReports = canViewAllCompetitorPriceReports || canViewMyCompetitorPriceReports;
    const actionCount = 1 + (canViewAnyCompetitorPriceReports ? 1 : 0);
    const [warehouses, setWarehouses] = useState([]);
    const [data, setData] = useState([]);
    const [updatingCanEditUnitPrices, setUpdatingCanEditUnitPrices] = useState([]);
    const [updatingPricePtcs, setUpdatingPricePtcs] = useState([]);
    const [removingWarehouses, setRemovingWarehouses] = useState({});
    const [removingSalesPersons, setRemovingSalesPersons] = useState([]);
    const [addingWarehouse, setAddingWarehouse] = useState([])
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('sales-person-access-levels-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.salesPersonAccessLevels);
        configureBehavioralTool((tags) => tags.salesPersonAccessLevels);
    }, [configureBehavioralTool, configureNotebookTags]);


    /**
     * Fetches list of all warehouses available for the operator
     * @return {Promise<void>}
     */
    const getAllWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * Fetches list of all warehouses available for the operator.
     */
    useEffect(() => {
        void getAllWarehouses();
    }, [getAllWarehouses])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: SalesPersonsAccessLevelsDataGridColumnNames.actions,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: (actionCount - 1) * 40,
                },
                {
                    title: "Name",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.name,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Code",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.code,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Operator Name",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.operatorName,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 150,
                },
                {
                    title: "Last Updated at",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.submittedDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.date,
                    sortable: false,
                },
                {
                    title: "Quote Count",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.quoteCount,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.number,
                    sortable: false,
                },
                {
                    title: "Sales Order Count",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.salesOrderCount,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.number,
                    sortable: false,
                },
                {
                    title: "Invoice Count",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.invoiceCount,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.number,
                    sortable: false,
                },
                {
                    title: "Change Unit Price",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.accessToPriceChange,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 150,
                },
                {
                    title: "Eligible Price Change %",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.eligiblePriceChangePct,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 150,
                },
                {
                    title: "Warehouse List",
                    name: SalesPersonsAccessLevelsDataGridColumnNames.warehouses,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        minWidth: 800,
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
            ],
        [actionCount])

    const [dataGridColumns, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    /**
     * Gets the list of freight regions by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @param cancelTokenSource
     * @param forExport
     * @param _paginationInfo
     * @return {Promise<ApiResponseDs<*> | void>}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const response = await Api.listSalesPersons(args.filters?.keyword ?? "");
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data ?? []);
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, setData])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Exports the list of freight regions in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportSalesPersonsAccessLevels = operatorAccessLevels.packages.base.exportSalesPersonsAccessLevels;
    const exportToCsv = useCallback(async () => {
        return {
            title: 'Sales Persons',
            body: data.map((e, i) => ({
                '': i + 1,
                "Name": e.name,
                "Code": e.code,
                "Operator Name": [e.operatorFirstName, e.operatorLastName].filter(n => n).join(' '),
                "Submitted DateTime": e.submittedDateTime,
                "Quote Count": e.quoteCount,
                "Sales Order Count": e.salesOrderCount,
                "Invoice Count": e.invoiceCount,
                "Access To Price Change": e.accessToPriceChange ? 'Yes' : 'No',
                "Eligible Price Change %": e.eligiblePriceChangePct ?? "N/A",
                "warehouses": e.warehouses.map(w => `${w.name} (${w.code})`).join(', '),
            })),
        }
    }, [data])

    /**
     * Removes a sales person from the list of sales persons
     * @type {(function(*): Promise<void>)|*}
     */
    const removeSalesPerson = useCallback(async (salesPerson) => {
        setRemovingSalesPersons(prevState => [...prevState, salesPerson.operatorId]);
        const response = await Api.deleteSalesPersonInformation(salesPerson.operatorId);
        if (!isMounted())
            return;
        setRemovingSalesPersons(prevState => prevState.filter(i => i !== salesPerson.operatorId));
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.operatorId !== salesPerson.operatorId));
        }
    }, [isMounted])

    /**
     * Gets users confirmation before removing the current order item.
     * @type {(item: any) => void}
     */
    const getRemoveConfirmation = useCallback((salesPerson) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <div className={'text-sm text-color-2'}>
                    Are you sure that you want to delete the sales person <strong>{salesPerson.name ?? ""}</strong> with code <strong>{salesPerson.code ?? ""}</strong>?
                </div>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeSalesPerson(salesPerson),
        );
    }, [removeSalesPerson]);

    /**
     * Updates the can-edit-unit-price status of a sales person
     * @type {(function(*, *): Promise<void>)}
     */
    const updateCanEditUnitPrice = useCallback(async ({operatorId, eligiblePriceChangePct}, val) => {
        setUpdatingCanEditUnitPrices(prevState => [...prevState, operatorId]);
        const response = await Api.updateSalesPersonConfig({
            operatorId: operatorId,
            accessToPriceChange: val,
            eligiblePriceChangePct: eligiblePriceChangePct,
        })
        if (!isMounted())
            return;
        setUpdatingCanEditUnitPrices(prevState => prevState.filter(i => i !== operatorId));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.operatorId === operatorId ? {...e, accessToPriceChange: val} : e));
        }
    }, [isMounted])


    /**
     * Saves the eligible price change percentage for a sales person
     * @type {(function(*, *): Promise<void>)|*}
     */
    const updateEligiblePriceChangePct = useCallback(async ({operatorId, accessToPriceChange}, value) => {
        if (!Number.isNaN(value) && value > 100) {
            value = 100;
        } else if (Number.isNaN(value) || value < 0) {
            value = 0;
        }
        setUpdatingPricePtcs(prevState => [...prevState, operatorId]);
        const response = await Api.updateSalesPersonConfig({
            operatorId: operatorId,
            accessToPriceChange: accessToPriceChange,
            eligiblePriceChangePct: value,
        });
        if (!isMounted())
            return;
        setUpdatingPricePtcs(prevState => prevState.filter(i => i !== operatorId));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.operatorId === operatorId ? {
                ...e,
                eligiblePriceChangePct: value
            } : e));
        }
    }, [isMounted])

    /**
     * Adds a warehouse to the sales person
     * @type {(function(*, *): Promise<void>)}
     */
    const addWarehouse = useCallback(async ({operatorId, warehouses}, warehouse) => {
        setAddingWarehouse(prevState => [...prevState, operatorId]);
        const response = await Api.grantSalesPersonWarehouseAccess({
            operatorId: operatorId,
            warehouseCodes: [...warehouses.map(w => w.code), warehouse.code],
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.operatorId === operatorId ? {
                ...e,
                warehouses: [...e.warehouses, warehouse]
            } : e));
        }
        setAddingWarehouse(prevState => prevState.filter(i => i !== operatorId));
    }, [isMounted])

    /**
     * Removes a warehouse from the sales person
     * @type {(function(*, *): Promise<void>)}
     */
    const removeWarehouse = useCallback(async ({operatorId, warehouses}, warehouse) => {
        setRemovingWarehouses(prevState => ({
            ...prevState,
            [operatorId]: [...(prevState[operatorId] ?? []), warehouse.id]
        }));
        const response = await Api.grantSalesPersonWarehouseAccess({
            operatorId: operatorId,
            warehouseCodes: warehouses.filter(w => w.id !== warehouse.id).map(w => w.code),
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.operatorId === operatorId ? {
                ...e,
                warehouses: e.warehouses.filter(w => w.id !== warehouse.id)
            } : e));
        }
        setRemovingWarehouses(prevState => ({
            ...prevState,
            [operatorId]: (prevState[operatorId] ?? []).filter(i => i !== warehouse.id)
        }));
    }, [isMounted])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(salesPerson => {
            const remainingWarehouses = warehouses.filter(warehouse => !salesPerson.warehouses.map(w => w.id).includes(warehouse.id));
            const removingSalesPerson = removingSalesPersons.includes(salesPerson.operatorId);
            const updatingCanEditUnitPrice = updatingCanEditUnitPrices.includes(salesPerson.operatorId);
            const updatingPricePtc = updatingPricePtcs.includes(salesPerson.operatorId);
            return ({
                key: salesPerson.operatorId,
                data: salesPerson,
                cells: {
                    [SalesPersonsAccessLevelsDataGridColumnNames.actions]:
                        <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'center'}}>
                            <Tooltip title={t('actions.remove')}>
                                <span>
                                    <IconButton
                                        onPressed={() => getRemoveConfirmation(salesPerson)}
                                        disabled={removingSalesPerson}
                                        color={'paper.error'}
                                        size={'medium'}
                                        aria-label={t('actions.remove')}
                                        className={'remove-icon'}
                                    >
                                        {removingSalesPerson
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <SvgIcon component={RemoveIcon} className={'icon medium'}/>}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {
                                (canViewAllCompetitorPriceReports
                                    || (competitorPriceReportsMineOnly && String(salesPerson.operatorId) === String(currentOperatorId))) &&
                                <Tooltip title={t('actions.viewReports')}>
                                    <Box component={'span'} sx={{display: 'inline-flex'}}>
                                        <CompetitorPriceReportsPopover
                                            operatorId={salesPerson.operatorId}
                                            operatorName={
                                                [salesPerson.operatorFirstName, salesPerson.operatorLastName].filter(Boolean).join(' ')
                                                || salesPerson.name
                                                || ''
                                            }
                                            mineOnly={competitorPriceReportsMineOnly}
                                            buttonProps={{'aria-label': t('actions.viewReports')}}
                                        >
                                            <AssessmentOutlined className={'icon medium'}/>
                                        </CompetitorPriceReportsPopover>
                                    </Box>
                                </Tooltip>
                            }
                        </Box>,
                    [SalesPersonsAccessLevelsDataGridColumnNames.name]: salesPerson.name,
                    [SalesPersonsAccessLevelsDataGridColumnNames.code]: salesPerson.code,
                    [SalesPersonsAccessLevelsDataGridColumnNames.operatorName]: [salesPerson.operatorFirstName, salesPerson.operatorLastName].filter(n => n).join(' '),
                    [SalesPersonsAccessLevelsDataGridColumnNames.submittedDateTime]: salesPerson.submittedDateTime,
                    [SalesPersonsAccessLevelsDataGridColumnNames.quoteCount]: salesPerson.quoteCount,
                    [SalesPersonsAccessLevelsDataGridColumnNames.salesOrderCount]: salesPerson.salesOrderCount,
                    [SalesPersonsAccessLevelsDataGridColumnNames.invoiceCount]: salesPerson.invoiceCount,
                    [SalesPersonsAccessLevelsDataGridColumnNames.accessToPriceChange]:
                        <div className={'d-flex align-items-center justify-content-end'}>
                            <Switch
                                color={'primary'}
                                checked={salesPerson.accessToPriceChange}
                                disabled={updatingCanEditUnitPrice || removingSalesPerson || updatingPricePtc}
                                onChange={(val) => updateCanEditUnitPrice(salesPerson, val)}
                            />
                            <Fade in={updatingCanEditUnitPrice} className={'ml-3'}>
                                <CircularProgress
                                    size={20}
                                    color={'secondary'}
                                />
                            </Fade>
                        </div>,
                    [SalesPersonsAccessLevelsDataGridColumnNames.eligiblePriceChangePct]:
                        salesPerson.accessToPriceChange
                            ? <TextField
                                size={'small'}
                                defaultValue={salesPerson.eligiblePriceChangePct ?? ""}
                                fullWidth
                                type={'number'}
                                disabled={removingSalesPerson || updatingPricePtc}
                                inputMode={'decimal'}
                                onBlur={(e) => updateEligiblePriceChangePct(
                                    salesPerson,
                                    e.target.value ? Number.isNaN(parseFloat(e.target.value)) ? null : parseFloat(e.target.value) : undefined
                                )}
                                InputProps={{
                                    endAdornment: updatingPricePtc
                                        ? <CircularProgress size={15} color={'primary'}/>
                                        : undefined
                                }}
                            /> : <Tooltip title={'Sales person does not have access to change unit price, so eligible price change percentage is not applicable.'}>
                                <Typography color={'text.disabled'} variant={'body2'}>
                                    {salesPerson.eligiblePriceChangePct ? `${salesPerson.eligiblePriceChangePct}%` : "N/A"}
                                </Typography>
                            </Tooltip>
                    ,
                    [SalesPersonsAccessLevelsDataGridColumnNames.warehouses]:
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                            {
                                salesPerson.warehouses.map(warehouse => (
                                    <Chips
                                        key={warehouse.id}
                                        iconPosition={ChipsIconPosition.start}
                                        contentType={ChipsContentType.text}
                                        content={`${warehouse.name} (${warehouse.code})`}
                                        disabled={removingSalesPerson}
                                        onIconClick={() => removeWarehouse(salesPerson, warehouse)}
                                        type={removingWarehouses[salesPerson.operatorId]?.includes(warehouse.id) ? ChipsTypes.loading : ChipsTypes.remove}
                                    />
                                ))
                            }
                            {
                                addingWarehouse.includes(salesPerson.operatorId) &&
                                <Skeleton
                                    width={100}
                                    height={42}
                                    sx={{transition: 'none'}}
                                />
                            }
                            {
                                !addingWarehouse.includes(salesPerson.operatorId) &&
                                remainingWarehouses.length > 0 &&
                                <TextField
                                    select
                                    value={''}
                                    disabled={removingSalesPerson}
                                    onChange={(e) => addWarehouse(salesPerson, e.target.value)}
                                    label={"New Warehouse"}
                                    size={'small'}
                                    sx={{
                                        minWidth: 200,
                                    }}
                                    SelectProps={{
                                        renderValue: (selected) => selected ? `${selected.name} (${selected.code})` : '',
                                    }}
                                >
                                    {
                                        remainingWarehouses?.map(warehouse => (
                                            <MenuItem key={warehouse.code} value={warehouse}>
                                                {warehouse.name} ({warehouse.code})
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            }
                        </Box>,
                }
            })
        })
    }, [addingWarehouse, canViewAllCompetitorPriceReports, competitorPriceReportsMineOnly, currentOperatorId, data, getRemoveConfirmation, removeWarehouse, addWarehouse, removingSalesPersons, removingWarehouses, t, updateCanEditUnitPrice, updateEligiblePriceChangePct, updatingCanEditUnitPrices, updatingPricePtcs, warehouses])

    /**
     * Searches over list of entities in the system by setting the query params according to the received keyword
     * @param keyword
     */
    const searchWithKeywords = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword ?? "",
        });
    }, [searchData]);


    return (
        <>
            <div className={'sales-persons-access-levels-view'}>
                <Card variant={'outlined'} sx={{padding: 2, border: 'none', display: 'flex', flexDirection: 'column', gap: 1}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0, alignItems: 'center'}}>
                        <SearchInput
                            loading={searchData.loading}
                            autoSearch={true}
                            search={searchWithKeywords}
                            label={'Search'}
                        />
                        <Divider flexItem orientation={'horizontal'}/>
                    </Box>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        hidePagination
                        hidePageSize
                        state={{
                            // sortBy: searchData.orderBy,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                            pagination: {pageSize: data.length, currentPage: 1, length: data.length},
                        }}
                        getExportInfo={canExportSalesPersonsAccessLevels ? exportToCsv : undefined}
                        // onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                        // onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        // onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                </Card>
            </div>
        </>
    )
}

export default SalesPersonsAccessLevelsView;
